import React from "react";
import { useTranslation } from "react-i18next";
import { safeParse } from "src/ui/utils/safeParse";

import { ConfirmDialog } from "@dashboard/devices/components/shared";
import { useUpdateProductMutation } from "@dashboard/products/api";
import { Product } from "@dashboard/products/types";
import { JsonTextarea } from "@dashboard/shared/components/JsonTextarea";
import { useCurrentWorkspace } from "@dashboard/workspaces/api/helpers";
import { Box, Typography, useTheme } from "@mui/material";
import CompareService from "@services/compareService/compareService";
import { PermissionService } from "@services/permissionService/permissionService";
import { PrimaryButton, SecondaryButton } from "@shared/CustomButton";
import { showSuccessToast } from "@shared/toasts/Toasts";
import { Tooltip } from "@shared/Tooltip";

import { useStyles } from "./AdvanceModeSection.style";
import Utils, { ValidationErrors } from "./AdvanceModeSection.utils";
import { ErrorSection, NoteSection, ParseErrorSection, WarningSection } from "./Sections";

type Props = {
    initialProduct: Product;
    initialValue: string;
    initialError: ValidationErrors[];
    initialWarning: string[];
    setIsAdvancedMode: (value: boolean) => void;
};
const AdvanceModeContent = (props: Props) => {
    const [value, setValue] = React.useState<string>(props.initialValue);
    const [validationError, setValidationError] = React.useState<ValidationErrors[]>(props.initialError);
    const [parseError, setParseError] = React.useState<string | null>(null);
    const [warning, setWarning] = React.useState<string[]>(props.initialWarning);
    const [isLoading, setIsLoading] = React.useState(false);
    const [isSaveDialogOpen, setIsSaveDialogOpen] = React.useState(false);

    const [updateProduct] = useUpdateProductMutation();

    const theme = useTheme();
    const { classes } = useStyles();
    const { t } = useTranslation(["common", "pages"]);

    const parsedValue = safeParse<Product | null>(value, null);
    const errorMessage = Utils.getFirstErrorMessage(parseError, validationError);

    const advancedModeDirty = new CompareService(props.initialValue, value).hasDifferences;

    const { data: workspace } = useCurrentWorkspace();
    const { productAbility } = PermissionService();
    const cannotManageProducts = productAbility(workspace).cannot("manage", "Product");
    const saveButtonDisabled = cannotManageProducts || !advancedModeDirty || isLoading || !!parseError;

    const onChange: React.ChangeEventHandler<HTMLTextAreaElement> = (e) => {
        try {
            const product: Product = JSON.parse(e.target.value);
            const validationErrors = Utils.getErrors(product);
            const unknownKeys = Utils.getWarnings(product);
            setParseError(null);
            setValidationError(validationErrors);
            setWarning(unknownKeys);
        } catch (_e) {
            setParseError(t("pages:productPage.advanceModeSection.advanceModeWarningInvalidJSON"));
        } finally {
            setValue(e.target.value);
        }
    };
    const onUpdate = async () => {
        setIsLoading(true);
        const result = await updateProduct({ ...parsedValue, codename: props.initialProduct.codename });
        if (!("error" in result)) {
            showSuccessToast(t("pages:productPage.toast.successfullySavedProduct"));
            props.setIsAdvancedMode(false);
        }
        setIsLoading(false);
    };
    const onCancel = () => {
        props.setIsAdvancedMode(false);
    };
    const onSave = async () => {
        if (validationError.length) {
            setIsSaveDialogOpen(true);
        } else {
            await onUpdate();
        }
    };
    const getSaveButtonTooltip = () => {
        if (errorMessage) {
            return t("pages:productPage.tooltip.resolveErrorsBeforeSaving");
        }
        if (!advancedModeDirty) {
            return t("pages:productPage.tooltip.noChangesToSave");
        }

        return t("pages:productPage.tooltip.saveChanges");
    };

    return (
        <React.Fragment>
            <Box className={classes.editorContainer}>
                <JsonTextarea value={value} onChange={onChange} style={{ marginBottom: theme.spacing(4) }} error={errorMessage} />
                <Box className={classes.buttonBox} sx={{ justifyContent: "space-between" }}>
                    <SecondaryButton onClick={onCancel} disabled={isLoading}>
                        {t("common:button.cancel")}
                    </SecondaryButton>

                    <Tooltip title={getSaveButtonTooltip()}>
                        <PrimaryButton disabled={saveButtonDisabled} onClick={onSave}>
                            {t("common:button.save")}
                        </PrimaryButton>
                    </Tooltip>
                </Box>
            </Box>
            <Box className={classes.notesContainer}>
                <NoteSection show={!parseError && !validationError.length && !warning.length} />
                <ParseErrorSection error={parseError} />
                <ErrorSection errors={validationError} />
                <WarningSection warnings={warning} />
            </Box>

            <ConfirmDialog
                title={t("pages:productPage.saveWithErrorsDialog.title")}
                primaryActionText={t("common:button.save")}
                secondaryActionText={t("common:button.cancel")}
                open={isSaveDialogOpen}
                onConfirm={onUpdate}
                onClose={() => setIsSaveDialogOpen(false)}
            >
                <Typography>{t("pages:productPage.saveWithErrorsDialog.message")}</Typography>
                <ErrorSection errors={validationError} />
            </ConfirmDialog>
        </React.Fragment>
    );
};

const AdvanceModeSection = (props: { initialValue: Product; setIsAdvancedMode: (value: boolean) => void }) => {
    const initialValue = JSON.stringify(props.initialValue, null, 2);

    const validationErrors = Utils.getErrors(props.initialValue);
    const unknownKeys = Utils.getWarnings(props.initialValue);

    return (
        <AdvanceModeContent
            // important: key is used to force re-render when the product id changes
            key={props.initialValue.id}
            initialValue={initialValue}
            initialError={validationErrors}
            initialWarning={unknownKeys}
            initialProduct={props.initialValue}
            setIsAdvancedMode={props.setIsAdvancedMode}
        />
    );
};

export default AdvanceModeSection;
